import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { useMst } from '../../../models/rootModel';
import { generateRandomEmail } from '../../../utils/dataGenerators/generateRandomEmail';
import { emailValidator } from '../../../utils/validators/emailValidator';
import { InputField } from '../../common/InputField/InputField';
import { BaseStepV2 } from '../../common/v2/BaseStepV2';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { StepV2MainContent } from '../../common/v2/StepV2MainContent';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import { ErrorInputText } from '../../styledComponent/SignUpComponents.style';
import { EmailConsent } from './EnterEmailStep.style';
import { ErrorType, recordError } from '../../../models/apiWrappers/api/exceptions';
import { useAnalytics } from '../../../utils/analytics';
import { setBrazeUserEmailAndSegment } from '../../../utils/braze';
import fbPixel from '../../../utils/fbPixel';
import { QuoraEventName } from '../../../models/types/useQuora.types';

interface EnterEmailStepProps extends BaseStepPropsV2 {}

const useMockedSignupData = process.env.REACT_APP_USE_MOCKED_SIGNUP_DATA === '1';

export const EnterEmailStep: React.FC<EnterEmailStepProps> = observer((props) => {
  const { id, nextStep, isActive, stepName } = props;

  const [email, setEmail] = useState(useMockedSignupData ? generateRandomEmail() : '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { UserDiscoveryResponse, UserPayment } = useMst();
  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      analytics.track(`view enter email`);
      return window.scrollTo(0, 0);
    }
  }, [analytics, isActive]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setEmail(value);
    setError('');
  };

  const canUseEmail = async () => {
    try {
      setIsLoading(true);
      const result = await UserDiscoveryResponse.checkIfEmailExists(email);

      return result;
    } catch (error) {
      recordError(ErrorType.API, error);

      return {
        ok: false,
        accountExistsError: false,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateEmail()) {
      return;
    }

    const emailExistsResult = await canUseEmail();

    if (!emailExistsResult) {
      setError('Unexpected server error');
      return;
    }

    if (emailExistsResult.ok || (!emailExistsResult.ok && emailExistsResult.accountExistsError)) {
      analytics.track(`enter email - submit email`, {
        email,
        stepName,
        segment: UserDiscoveryResponse.segment,
        location: 'webOnboarding',
        healingProgram: UserPayment.enableProgram ? 'yes' : 'no',
      });
      fbPixel.trackContact(email);

      UserDiscoveryResponse.setUserEmail(email);
      setBrazeUserEmailAndSegment(email, UserDiscoveryResponse.segment, UserPayment.enableProgram);

      // in this point value (price) is unknown. 10$ value reflects the average cost per lead for Web
      UserDiscoveryResponse.sendQuoraConversionEvent({ eventName: QuoraEventName.GeneratedLead, value: 10 });

      nextStep();
    }

    if (!emailExistsResult.ok && !emailExistsResult.accountExistsError) {
      setError('Unexpected server error');
    }
  };

  const validateEmail = (): boolean => {
    const result = emailValidator(email);

    if (result.error) {
      setError(result.errorMessage);
    }

    return !result.error;
  };

  return (
    <BaseStepV2 {...props}>
      <StepV2MainContent
        {...props}
        isButtonDisabled={Boolean(isLoading || error || !email)}
        isLoading={isLoading}
        onButtonClick={handleSubmit}
        buttonVariant={MainButtonVariant.Black}
      >
        <InputField
          id={id}
          type="email"
          name="email"
          handleChange={handleChange}
          value={email}
          placeholder="name@email.com"
          error={error}
        />
        {error && <ErrorInputText>{error}</ErrorInputText>}

        <EmailConsent>
          By submitting your email you agree to get emails from us. We're here to support mental health, we promise we
          won't drive you crazy
        </EmailConsent>
      </StepV2MainContent>
    </BaseStepV2>
  );
});
